import React, { useState, useCallback, useMemo, isValidElement } from 'react';

export interface CollapseProps {
  activeKey?: string[];
  defaultActiveKey?: Array<string | number> | string | number;
  accordion?: boolean;
  onChange?: (panelKey: string | string[]) => void;
  collapsible?: boolean;
  children: React.ReactNode | React.ReactNode[];
}

export const Collapse = ({
  children,
  accordion,
  activeKey = [],
}: CollapseProps) => {
  const [activeKeys, setActiveKey] = useState<string[]>(activeKey);

  const onItemClick = useCallback(
    (panelKey: string, e: MouseEvent) => {
      e.stopPropagation();

      if (accordion) {
        return setActiveKey(activeKeys[0] === panelKey ? [] : [panelKey]);
      }

      return setActiveKey((prevState: string[]) => {
        const index = activeKeys.indexOf(panelKey);
        const isActive = index > -1;

        if (isActive) {
          return prevState.filter((key) => key !== panelKey);
        }
        return [...prevState, panelKey];
      });
    },
    [activeKeys, accordion]
  );

  const panels = useMemo(() => {
    return React.Children.toArray(children).map((child, index) => {
      const panelKey = String(index);
      if (isValidElement(child)) {
        const panelProps = {
          ...child.props,
          isActive: accordion
            ? activeKeys[0] === panelKey
            : activeKeys.indexOf(panelKey) > -1,
          panelKey,
          panelIndex: String(index),
          onItemClick,
        };
        if (typeof child.type === 'string') {
          return child;
        }
        return React.cloneElement(child, panelProps);
      }
      return child;
    });
  }, [accordion, activeKeys, children, onItemClick]);

  return <div>{panels}</div>;
};
